import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './Header.module.css';
import { useLang } from '../context/LanguageContext';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen]   = useState(false);
  const location = useLocation();
  const { lang, toggle, t } = useLang();

  const navLinks = [
    { to: '/',         label: t.nav.home     },
    { to: '/services', label: t.nav.services },
    { to: '/projects', label: t.nav.projects },
    { to: '/about',    label: t.nav.about    },
    { to: '/contact',  label: t.nav.contact  },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setIsOpen(false); }, [location]);

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <>
      <motion.header
        className={`${styles.header} ${scrolled ? styles.scrolled : ''}`}
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
      >
        <Link to="/" className={styles.logo}>
          <img src="/logo/axisdev-logo.png" alt="AxisDev" className={styles.logoImg} />
        </Link>

        <nav className={styles.nav}>
          {navLinks.map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              className={`${styles.navLink} ${isActive(to) ? styles.active : ''}`}
            >
              {label}
              {isActive(to) && (
                <motion.span
                  className={styles.navIndicator}
                  layoutId="nav-indicator"
                  transition={{ type: 'spring', stiffness: 420, damping: 32 }}
                />
              )}
            </Link>
          ))}
        </nav>

        <div className={styles.actions}>
          <button className={styles.langToggle} onClick={toggle} aria-label="Switch language">
            {lang === 'fr' ? 'EN' : 'FR'}
          </button>
          <Link to="/contact" className={styles.ctaBtn}>
            {t.nav.cta}
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </Link>
          <button
            className={`${styles.burger} ${isOpen ? styles.burgerOpen : ''}`}
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Menu"
          >
            <span /><span /><span />
          </button>
        </div>
      </motion.header>

      {/* Mobile menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className={styles.mobileMenu}
            initial={{ opacity: 0, y: -8, scaleY: 0.97 }}
            animate={{ opacity: 1, y: 0, scaleY: 1 }}
            exit={{ opacity: 0, y: -8, scaleY: 0.97 }}
            transition={{ duration: 0.18 }}
          >
            {navLinks.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className={`${styles.mobileLink} ${isActive(to) ? styles.mobileLinkActive : ''}`}
              >
                {label}
              </Link>
            ))}
            <Link to="/contact" className={styles.mobileCta}>{t.nav.cta} →</Link>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
